import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Trash2, DollarSign, Percent, Download, Send, Loader2, X } from 'lucide-react';

const ITEM_TYPES = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'once_off', label: 'Once-off' },
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyLineItem = () => ({
  id: '',
  description: '',
  item_type: 'recurring',
  frequency: 'monthly',
  quantity: 1,
  unit_price: 0,
  notes: '',
});

const QuoteModal = ({ open, quote, opportunity, companies, contacts, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    opportunity_id: '',
    company_id: '',
    contact_id: '',
    valid_days: 30,
    discount_percent: 0,
    setup_fee: 0,
    setup_fee_description: 'Setup & Onboarding Fee',
    terms_and_conditions: '',
    notes: '',
    line_items: [emptyLineItem()],
  });
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendData, setSendData] = useState({ recipient_email: '', cc_emails: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (quote) {
      setFormData({
        title: quote.title || '',
        opportunity_id: quote.opportunity_id || '',
        company_id: quote.company_id || '',
        contact_id: quote.contact_id || '',
        valid_days: quote.valid_days || 30,
        discount_percent: quote.discount_percent || 0,
        setup_fee: quote.setup_fee || 0,
        setup_fee_description: quote.setup_fee_description || 'Setup & Onboarding Fee',
        terms_and_conditions: quote.terms_and_conditions || '',
        notes: quote.notes || '',
        line_items: quote.line_items?.length > 0
          ? quote.line_items.map(li => ({ ...li }))
          : [emptyLineItem()],
      });
    } else if (opportunity) {
      setFormData({
        title: `Quote - ${opportunity.name}`,
        opportunity_id: opportunity.id || '',
        company_id: opportunity.company_id || '',
        contact_id: opportunity.contact_id || '',
        valid_days: 30,
        discount_percent: 0,
        setup_fee: 0,
        setup_fee_description: 'Setup & Onboarding Fee',
        terms_and_conditions: '',
        notes: '',
        line_items: [emptyLineItem()],
      });
    } else {
      setFormData({
        title: '',
        opportunity_id: '',
        company_id: '',
        contact_id: '',
        valid_days: 30,
        discount_percent: 0,
        setup_fee: 0,
        setup_fee_description: 'Setup & Onboarding Fee',
        terms_and_conditions: '',
        notes: '',
        line_items: [emptyLineItem()],
      });
    }
    setShowSendDialog(false);
  }, [quote, opportunity, open]);

  const totals = useMemo(() => {
    let monthly = 0, yearly = 0, onceOff = 0;
    formData.line_items.forEach(li => {
      const lineTotal = (li.quantity || 0) * (li.unit_price || 0);
      if (li.item_type === 'once_off') {
        onceOff += lineTotal;
      } else if (li.frequency === 'monthly') {
        monthly += lineTotal;
      } else {
        yearly += lineTotal;
      }
    });
    const yearlyTotal = monthly * 12 + yearly;
    const disc = formData.discount_percent || 0;
    const yearlyDiscounted = yearlyTotal * (1 - disc / 100);
    const savings = yearlyTotal - yearlyDiscounted;
    const onceOffWithSetup = onceOff + (formData.setup_fee || 0);
    return {
      monthly: monthly.toFixed(2),
      yearlyTotal: yearlyTotal.toFixed(2),
      yearlyDiscounted: yearlyDiscounted.toFixed(2),
      savings: savings.toFixed(2),
      onceOff: onceOffWithSetup.toFixed(2),
      grandYearly: (yearlyDiscounted + onceOffWithSetup).toFixed(2),
    };
  }, [formData.line_items, formData.discount_percent, formData.setup_fee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Quote title is required');
      return;
    }
    if (formData.line_items.length === 0 || !formData.line_items.some(li => li.description.trim())) {
      toast.error('At least one line item with a description is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.filter(li => li.description.trim()),
      };
      if (!payload.opportunity_id) delete payload.opportunity_id;
      if (!payload.company_id) delete payload.company_id;
      if (!payload.contact_id) delete payload.contact_id;

      if (quote?.id) {
        await api.crm.updateQuote(quote.id, payload);
        toast.success('Quote updated');
      } else {
        await api.crm.createQuote(payload);
        toast.success('Quote created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!quote?.id) return;
    if (!window.confirm('Delete this quote?')) return;
    setLoading(true);
    try {
      await api.crm.deleteQuote(quote.id);
      toast.success('Quote deleted');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to delete quote');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!quote?.id) return;
    setDownloadingPdf(true);
    try {
      const res = await api.crm.downloadQuotePdf(quote.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quote.quote_number || 'Quote'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleOpenSendDialog = () => {
    // Pre-fill recipient from contact email
    const contact = contacts.find(c => c.id === (quote?.contact_id || formData.contact_id));
    setSendData({
      recipient_email: contact?.email || '',
      cc_emails: '',
      message: '',
    });
    setShowSendDialog(true);
  };

  const handleSendQuote = async () => {
    if (!sendData.recipient_email.trim()) {
      toast.error('Recipient email is required');
      return;
    }
    setSending(true);
    try {
      const ccList = sendData.cc_emails
        .split(',')
        .map(e => e.trim())
        .filter(e => e);
      await api.crm.sendQuote(quote.id, {
        recipient_email: sendData.recipient_email.trim(),
        cc_emails: ccList,
        message: sendData.message,
      });
      toast.success(`Quote sent to ${sendData.recipient_email}`);
      setShowSendDialog(false);
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setSending(false);
    }
  };

  const updateLineItem = (idx, field, value) => {
    const updated = [...formData.line_items];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, line_items: updated });
  };

  const addLineItem = () => {
    setFormData({ ...formData, line_items: [...formData.line_items, emptyLineItem()] });
  };

  const removeLineItem = (idx) => {
    const updated = formData.line_items.filter((_, i) => i !== idx);
    setFormData({ ...formData, line_items: updated.length > 0 ? updated : [emptyLineItem()] });
  };

  const filteredContacts = formData.company_id
    ? contacts.filter(c => c.company_id === formData.company_id)
    : contacts;

  // Send Dialog overlay
  if (showSendDialog && quote?.id) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" />
              Send Quote {quote.quote_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={sendData.recipient_email}
                onChange={(e) => setSendData({ ...sendData, recipient_email: e.target.value })}
                placeholder="client@example.com"
                data-testid="send-quote-email-input"
              />
            </div>
            <div>
              <Label>CC (comma-separated)</Label>
              <Input
                value={sendData.cc_emails}
                onChange={(e) => setSendData({ ...sendData, cc_emails: e.target.value })}
                placeholder="manager@example.com, accounts@example.com"
                data-testid="send-quote-cc-input"
              />
            </div>
            <div>
              <Label>Personal Message (optional)</Label>
              <Textarea
                value={sendData.message}
                onChange={(e) => setSendData({ ...sendData, message: e.target.value })}
                placeholder="Add a personal message to include in the email..."
                rows={3}
                data-testid="send-quote-message-input"
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p className="font-medium text-slate-800 mb-1">What will be sent:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Professional branded email with quote summary</li>
                <li>PDF attachment: {quote.quote_number}.pdf</li>
                <li>Quote status will be updated to "Sent"</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} data-testid="cancel-send-quote-btn">
              <X className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button onClick={handleSendQuote} disabled={sending} data-testid="confirm-send-quote-btn">
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send Quote</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            {quote ? 'Edit Quote' : 'New Quote'}
            {quote?.quote_number && (
              <Badge variant="secondary" className="ml-2">{quote.quote_number}</Badge>
            )}
            {quote?.status && (
              <Badge
                variant="secondary"
                className={
                  quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                  quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                  quote.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  quote.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                  'bg-amber-100 text-amber-700'
                }
              >
                {quote.status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Header Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Quote Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Monthly Retainer - ABC Construction"
                data-testid="quote-title-input"
              />
            </div>
            <div>
              <Label>Company</Label>
              <Select
                value={formData.company_id || '__none__'}
                onValueChange={(v) => setFormData({ ...formData, company_id: v === '__none__' ? '' : v, contact_id: '' })}
              >
                <SelectTrigger data-testid="quote-company-select">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact</Label>
              <Select
                value={formData.contact_id || '__none__'}
                onValueChange={(v) => setFormData({ ...formData, contact_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger data-testid="quote-contact-select">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No contact</SelectItem>
                  {filteredContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valid For (days)</Label>
              <Input
                type="number"
                value={formData.valid_days}
                onChange={(e) => setFormData({ ...formData, valid_days: parseInt(e.target.value) || 30 })}
                data-testid="quote-valid-days-input"
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold">Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-3">
              {formData.line_items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg border" data-testid={`line-item-${idx}`}>
                  <div className="col-span-4">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                      placeholder="Service description"
                      data-testid={`line-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Type</Label>
                    <Select value={item.item_type} onValueChange={(v) => updateLineItem(idx, 'item_type', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ITEM_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {item.item_type === 'recurring' && (
                    <div className="col-span-2">
                      <Label className="text-xs">Frequency</Label>
                      <Select value={item.frequency} onValueChange={(v) => updateLineItem(idx, 'frequency', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {item.item_type === 'once_off' && <div className="col-span-2" />}
                  <div className="col-span-1">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Unit Price (R)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      data-testid={`line-item-price-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(idx)}
                      className="text-red-400 hover:text-red-600 h-9"
                      data-testid={`remove-line-item-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="col-span-12 text-right text-sm font-medium text-slate-600">
                    Line Total: R {((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Setup Fee & Discount */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5" /> Setup / Onboarding Fee (R)
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.setup_fee}
                onChange={(e) => setFormData({ ...formData, setup_fee: parseFloat(e.target.value) || 0 })}
                data-testid="quote-setup-fee-input"
              />
            </div>
            <div>
              <Label>Setup Fee Description</Label>
              <Input
                value={formData.setup_fee_description}
                onChange={(e) => setFormData({ ...formData, setup_fee_description: e.target.value })}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <Percent className="h-3.5 w-3.5" /> Yearly Discount (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                data-testid="quote-discount-input"
              />
            </div>
          </div>

          {/* Totals Summary */}
          <div className="bg-slate-900 text-white rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-3 text-slate-300 uppercase tracking-wider">Quote Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Monthly Total</p>
                <p className="text-xl font-bold">R {parseFloat(totals.monthly).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-slate-400">Yearly Total</p>
                <p className="text-lg font-semibold">R {parseFloat(totals.yearlyTotal).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-slate-400">Yearly with {formData.discount_percent}% Discount</p>
                <p className="text-lg font-semibold text-green-400">R {parseFloat(totals.yearlyDiscounted).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                {parseFloat(totals.savings) > 0 && (
                  <p className="text-xs text-green-300">Save R {parseFloat(totals.savings).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                )}
              </div>
              <div>
                <p className="text-slate-400">Once-off (incl. Setup)</p>
                <p className="text-lg font-semibold">R {parseFloat(totals.onceOff).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="col-span-2">
                <p className="text-slate-400">Grand Total (Yearly + Once-off)</p>
                <p className="text-2xl font-bold text-amber-400">R {parseFloat(totals.grandYearly).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>

          {/* Terms & Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Terms & Conditions</Label>
              <Textarea
                value={formData.terms_and_conditions}
                onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
                placeholder="Payment terms, cancellation policy..."
                rows={3}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Internal notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              {quote?.id && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading} data-testid="delete-quote-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {quote?.id && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    data-testid="download-quote-pdf-btn"
                  >
                    {downloadingPdf ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    PDF
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleOpenSendDialog}
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                    data-testid="send-quote-btn"
                  >
                    <Send className="h-4 w-4 mr-2" /> Send
                  </Button>
                </>
              )}
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-quote-btn">
                {loading ? 'Saving...' : (quote ? 'Update Quote' : 'Create Quote')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
